import { Form, Spin, Switch } from 'antd'
import ListContentLayout from 'layout/ListContentLayout'
import { FC, useEffect, useState } from 'react'
import { TLeadEngagement } from 'features/automation/types'
import {
 useGetLeadEngagementAlertQuery,
 useSmartLeadEngagementStoreMutation,
} from 'features/automation/api'
import {
 AiBanner,
 AiCard,
 AiFooter,
 AiPageHeader,
 AiSliderBody,
 NotifBtn,
 NotifBtns,
 TBandConfig,
 getBandLabel,
} from '../components/AiModule'

// ── Slider config per field ──────────────────────────────────────────

const SLIDER_CFGS: Record<string, TBandConfig> = {
 active_after_hours: {
  min: 1,
  max: 23,
  labels: ['Immediate', 'Standard', 'Relaxed', 'Passive'],
  engageLabel: 'Response speed',
  adaptiveNote: '⚡ Notification timing adapts based on lead engagement level',
 },
 active_after_days: {
  min: 2,
  max: 364,
  labels: ['Short', 'Moderate', 'Long', 'Extended'],
  engageLabel: 'Dormancy window',
  adaptiveNote:
   '⚡ AI detects returning leads and alerts you when re-engagement is meaningful',
 },
 inactive_after_days: {
  min: 7,
  max: 99,
  labels: ['Quick', 'Moderate', 'Patient', 'Very Patient'],
  engageLabel: 'Inactivity threshold',
  adaptiveNote:
   '⚡ AI adjusts inactivity detection based on typical lead behaviour in your market',
 },
 same_property_views: {
  min: 2,
  max: 99,
  labels: ['Curious', 'Interested', 'Serious', 'Committed'],
  engageLabel: 'Repeat interest',
  adaptiveNote:
   '⚡ AI triggers alert when repeat views indicate serious purchase consideration',
 },
 different_properties_views: {
  min: 2,
  max: 99,
  labels: ['Narrow', 'Moderate', 'Broad', 'Very Broad'],
  engageLabel: 'Search breadth',
  adaptiveNote:
   '⚡ AI detects comparison shopping behaviour — a strong signal of active decision-making',
 },
}

// ── Website slider rows config ───────────────────────────────────────

type TSliderRow = {
 title: string
 chip: { label: string; variant?: 'green' | 'amber' }
 field: keyof TLeadEngagement
 emailField: keyof TLeadEngagement
 pushField: keyof TLeadEngagement
}

const WEBSITE_SLIDERS: TSliderRow[] = [
 {
  title: 'Notify me when lead becomes active',
  chip: { label: '⚡ High intent signal', variant: 'green' },
  field: 'active_after_hours',
  emailField: 'active_after_hours_email_notification',
  pushField: 'active_after_hours_push_notification',
 },
 {
  title: 'Notify me when a dormant lead re-engages',
  chip: { label: '⚡ Re-engagement signal', variant: 'green' },
  field: 'active_after_days',
  emailField: 'active_after_days_email_notification',
  pushField: 'active_after_days_push_notification',
 },
 {
  title: 'Notify me when lead goes inactive',
  chip: { label: '⚠ Cold lead warning', variant: 'amber' },
  field: 'inactive_after_days',
  emailField: 'inactive_after_days_email_notification',
  pushField: 'inactive_after_days_push_notification',
 },
 {
  title: 'Notify me when lead repeatedly views the same property',
  chip: { label: '🔴 Strong buying intent' },
  field: 'same_property_views',
  emailField: 'same_property_views_email_notification',
  pushField: 'same_property_views_push_notification',
 },
 {
  title: 'Notify me when lead is actively browsing multiple properties',
  chip: { label: '⚡ Actively searching', variant: 'green' },
  field: 'different_properties_views',
  emailField: 'different_properties_views_email_notification',
  pushField: 'different_properties_views_push_notification',
 },
]

// ── Email rows with bidirectional sync ───────────────────────────────

type TSyncedRow = {
 label: string
 chip: { label: string; variant?: 'green' | 'amber' }
 mainField: keyof TLeadEngagement
 emailField: keyof TLeadEngagement
 pushField: keyof TLeadEngagement
}

const EMAIL_SYNCED_ROWS: TSyncedRow[] = [
 {
  label: 'Notify me when lead clicks a link in email',
  chip: { label: '⚡ Strong engagement', variant: 'green' },
  mainField: 'clicked_email_link',
  emailField: 'clicked_email_link_email_notification',
  pushField: 'clicked_email_link_push_notification',
 },
 {
  label: 'Notify me when lead opens an email',
  chip: { label: '👁 Awareness signal', variant: 'amber' },
  mainField: 'opened_email',
  emailField: 'opened_email_email_notification',
  pushField: 'opened_email_push_notification',
 },
 {
  label: 'Notify me when lead unsubscribes from email',
  chip: { label: '🔴 Action required' },
  mainField: 'unsubscribed_email',
  emailField: 'unsubscribed_email_email_notification',
  pushField: 'unsubscribed_email_push_notification',
 },
]

const initialValues: TLeadEngagement = {}

const LeadEngagementAlert: FC = () => {
 const [SmartLeadEngagementStore, { isLoading }] =
  useSmartLeadEngagementStoreMutation()
 const [SmartLeadEngagementStoreAll, { isLoading: isLoadingAll }] =
  useSmartLeadEngagementStoreMutation()
 const [status, setStatus] = useState(false)
 const {
  data,
  refetch,
  isLoading: dataLoading,
 } = useGetLeadEngagementAlertQuery(null)
 const [form] = Form.useForm<TLeadEngagement>()

 // Watch only the slider fields needed for band label display
 const activeHrs = Form.useWatch('active_after_hours', form) ?? 4
 const activeDays = Form.useWatch('active_after_days', form) ?? 30
 const inactiveDays = Form.useWatch('inactive_after_days', form) ?? 30
 const sameViews = Form.useWatch('same_property_views', form) ?? 3
 const diffViews = Form.useWatch('different_properties_views', form) ?? 5

 const sliderValues: Record<string, number> = {
  active_after_hours: activeHrs,
  active_after_days: activeDays,
  inactive_after_days: inactiveDays,
  same_property_views: sameViews,
  different_properties_views: diffViews,
 }

 useEffect(() => {
  form.setFieldsValue(data ? { ...initialValues, ...data } : initialValues)
  if (data) setStatus(data.status === 1)
 }, [data])

 const handleToggle = async (val: boolean) => {
  setStatus(val)
  await SmartLeadEngagementStore({
   ...form.getFieldsValue(),
   status: val ? 1 : 0,
  }).unwrap()
  refetch()
 }

 const handleSave = async () => {
  const values = await form.validateFields()
  await SmartLeadEngagementStoreAll({
   ...values,
   status: status ? 1 : 0,
  }).unwrap()
  refetch()
 }

 const handleCancel = () => {
  form.setFieldsValue(data ? { ...initialValues, ...data } : initialValues)
 }

 return (
  <div className="smart-engegement-page">
   <div className="smart-engegement-page-body">
    <Spin spinning={dataLoading}>
     <Form form={form} initialValues={initialValues}>
      <div className={`ai-module-page${!status ? ' disabled' : ''}`}>
       <AiPageHeader
        title="AI Lead Engagement Intelligence"
        toggleLabel="AI Lead Engagement"
        status={status}
        loading={isLoading}
        onToggle={handleToggle}
       />

       <AiBanner title="Intelligent Lead Activity Tracking">
        <p>
         AI Lead Engagement Intelligence monitors lead behaviour across your
         website, emails, and SMS — and notifies you at the right moment to act.
         Track when leads become active or go cold, interact with properties,
         click email links, unsubscribe, or reply to SMS. Notifications are
         delivered via email or push (App) — configure each trigger
         independently.
        </p>
       </AiBanner>

       {/* Website Engagement */}
       <AiCard
        title="AI Website Engagement"
        tag="Website Signals"
        icon="🌐"
        sectionNote="🧠 AI highlights high-intent signals to help you prioritise follow-ups at the right time."
       >
        {WEBSITE_SLIDERS.map((row) => (
         <SliderRow
          key={String(row.field)}
          row={row}
          value={sliderValues[String(row.field)]}
          disabled={!status}
         />
        ))}

        {/* Subscribed to listing alert — no slider, always-on */}
        <ReadonlyEngRow
         label="Notify me when lead subscribes to a Listing Alert"
         chip={{ label: '⚡ High intent action', variant: 'green' }}
         mainField="subscribed_listing_alert"
         emailField="subscribed_listing_alert_email_notification"
         pushField="subscribed_listing_alert_push_notification"
         disabled
        />
       </AiCard>

       {/* Email Engagement */}
       <AiCard
        title="AI Email Engagement"
        tag="Email Signals"
        icon="✉"
        sectionNote="🧠 AI tracks email interaction patterns to detect when a lead is warming up or disengaging."
       >
        {EMAIL_SYNCED_ROWS.map((row) => (
         <SyncedEngRow
          key={String(row.mainField)}
          row={row}
          disabled={!status}
         />
        ))}
        <ReadonlyEngRow
         label="Notify me when lead replies to email"
         chip={{ label: '⚡ Direct response', variant: 'green' }}
         mainField="replied_email"
         emailField="replied_email_email_notification"
         pushField="replied_email_push_notification"
         disabled
        />
       </AiCard>

       {/* SMS Engagement */}
       <AiCard
        title="AI SMS Engagement"
        tag="SMS Signals"
        icon="📱"
        sectionNote="🧠 SMS replies are high-intent signals — AI flags these immediately for fast follow-up."
       >
        <ReadonlyEngRow
         label="Notify me when lead replies to SMS"
         chip={{ label: '⚡ Direct response', variant: 'green' }}
         mainField="replied_sms"
         emailField="replied_sms_email_notification"
         pushField="replied_sms_push_notification"
         disabled
        />
       </AiCard>
      </div>
     </Form>
    </Spin>
   </div>
   {status && (
    <AiFooter
     saving={isLoadingAll}
     onCancel={handleCancel}
     onSave={handleSave}
    />
   )}
  </div>
 )
}

export default LeadEngagementAlert

// ── Sub-components ───────────────────────────────────────────────────

const SliderRow: FC<{ row: TSliderRow; value: number; disabled: boolean }> = ({
 row,
 value,
 disabled,
}) => {
 const cfg = SLIDER_CFGS[String(row.field)]
 return (
  <div className="ai-slider-section-row">
   <div className="ai-slider-top">
    <span className="ai-slider-title">{row.title}</span>
    <span
     className={`ai-chip${row.chip.variant ? ` ${row.chip.variant}` : ''}`}
    >
     {row.chip.label}
    </span>
    <NotifBtns
     emailField={String(row.emailField)}
     pushField={String(row.pushField)}
     disabled={disabled}
    />
   </div>
   <AiSliderBody
    field={String(row.field)}
    cfg={cfg}
    bandLabel={getBandLabel(value, cfg)}
    disabled={disabled}
   />
  </div>
 )
}

// Row where main switch syncs both notif buttons bidirectionally.
// Uses Form.useFormInstance() to read sibling field values without watching everything.
const SyncedEngRow: FC<{ row: TSyncedRow; disabled: boolean }> = ({
 row,
 disabled,
}) => {
 const form = Form.useFormInstance<TLeadEngagement>()
 const { mainField, emailField, pushField } = row

 return (
  <div className="ai-eng-row">
   <Form.Item
    name={mainField}
    noStyle
    valuePropName="checked"
    getValueFromEvent={(v: boolean) => {
     form.setFieldsValue({ [emailField]: v, [pushField]: v })
     return v
    }}
   >
    <Switch disabled={disabled} style={{ flexShrink: 0 }} />
   </Form.Item>
   <span className="ai-eng-label">{row.label}</span>
   <span className={`ai-chip${row.chip.variant ? ` ${row.chip.variant}` : ''}`}>
    {row.chip.label}
   </span>
   <div className="ai-notif-btns">
    <Form.Item
     name={emailField}
     noStyle
     getValueFromEvent={(v: boolean) => {
      if (v) form.setFieldsValue({ [mainField]: true })
      else if (!form.getFieldValue(pushField))
       form.setFieldsValue({ [mainField]: false })
      return v
     }}
    >
     <NotifBtn label="Email" disabled={disabled} />
    </Form.Item>
    <Form.Item
     name={pushField}
     noStyle
     getValueFromEvent={(v: boolean) => {
      if (v) form.setFieldsValue({ [mainField]: true })
      else if (!form.getFieldValue(emailField))
       form.setFieldsValue({ [mainField]: false })
      return v
     }}
    >
     <NotifBtn label="App" disabled={disabled} />
    </Form.Item>
   </div>
  </div>
 )
}

// Row where main switch is always-on (read-only enable state).
// mainField must be provided so its value is tracked in the form and submitted.
const ReadonlyEngRow: FC<{
 label: string
 chip: { label: string; variant?: 'green' | 'amber' }
 mainField: string
 emailField: string
 pushField: string
 disabled: boolean
}> = ({ label, chip, mainField, emailField, pushField, disabled }) => (
 <div className="ai-eng-row">
  <Form.Item name={mainField} noStyle valuePropName="checked">
   <Switch disabled style={{ flexShrink: 0 }} />
  </Form.Item>
  <span className="ai-eng-label">{label}</span>
  <span className={`ai-chip${chip.variant ? ` ${chip.variant}` : ''}`}>
   {chip.label}
  </span>
  <NotifBtns
   emailField={emailField}
   pushField={pushField}
   disabled={disabled}
  />
 </div>
)
